import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Form as AntdForm } from 'antd';
import { routes } from '../../../../../utils/helpers/ApiClient';
import AppBarCMS from '../../../../basic/Navigation/AppBarCMS/AppBarCMS';
import PhdPageContext from '../../../../common/Phd/PhdPageContext.jsx';
import { AdminContentBlock, Spinner } from '../../../../common';
import Form from '../../../../antd/form/Form/Form';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import { applyPhdValidationErrors } from '../utils/phdApiFeedback.js';
import {
  useGetPhdDissertationDefenseExaminerProposalQuery,
  useSavePhdDissertationDefenseExaminerProposalMutation,
  useLazyGetPhdDissertationDefenseExaminerProposalDocumentQuery,
  useArchivePhdDissertationDefenseExaminerProposalDocumentMutation,
  useGetSubmittedPhdDissertationDefenseApplicationsQuery,
} from '../../../../../redux/services/phd';
import { usePhdReferenceData } from '../usePhdReferenceData.jsx';
import { usePhdDashboardPrefill } from '../utils/usePhdDashboardPrefill.js';
import PhdDissertationDefenseExaminerProposalsEditForm from './PhdDissertationDefenseExaminerProposalsEditForm.jsx';
import { usePhdDirtyLeaveGuard } from '../usePhdDirtyLeaveGuard.jsx';
import { downloadBlobResponse } from '../../../../../utils/helpers/downloadBlobResponse';
import PhdArchivedRecordNotice from '../PhdArchivedRecordNotice.jsx';
import useIsUserPhdAdmin from '../../../../../hooks/useIsUserPhdAdmin.jsx';

const PhdDissertationDefenseExaminerProposalsEdit = ({ mode }) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = AntdForm.useForm();
  const isCreate = mode === 'create';
  const dashboardPrefill = usePhdDashboardPrefill();
  const isAdmin = useIsUserPhdAdmin();
  const isEdit = mode === 'edit';
  const pageTitle = isCreate
    ? t('Pridanie návrhu na oponentov (obhajoba)')
    : t('Editácia návrhu na oponentov (obhajoba)');

  useEffect(() => {
    document.title = `${pageTitle} - FRI CMS`;
  }, [pageTitle]);

  const { data: detail, isLoading } =
    useGetPhdDissertationDefenseExaminerProposalQuery(id, {
      skip: !isEdit,
    });
  const [saveProposal, { isLoading: isSaving }] =
    useSavePhdDissertationDefenseExaminerProposalMutation();
  const [downloadProposalDoc, { isLoading: isDownloading }] =
    useLazyGetPhdDissertationDefenseExaminerProposalDocumentQuery();
  const [archiveProposalDoc, { isLoading: isArchiving }] =
    useArchivePhdDissertationDefenseExaminerProposalDocumentMutation();
  const { saveSuccess, saveFailed } = useToastMessages();
  const { data: submittedApplications = [], isLoading: isLoadingApplications } =
    useGetSubmittedPhdDissertationDefenseApplicationsQuery();

  const {
    studentOptions,
    students,
    schoolYearOptions,
    examinerOptions,
    studyFieldLabelOptions,
    ensureSchoolYearSelection,
    isCreatingSchoolYear,
    isLoading: isReferenceLoading,
  } = usePhdReferenceData({ includeExaminers: true });

  const initials = useMemo(() => {
    if (isCreate) {
      return {
        phd_student_id: dashboardPrefill.phd_student_id,
        phd_dissertation_defense_application_id:
          dashboardPrefill.phd_dissertation_defense_application_id,
        prof_opponent_id: undefined,
        qualified_opponent_id: undefined,
        qualified_opponent_2_id: undefined,
        student_display_name: undefined,
        supervisor_display_name: undefined,
        prof_opponent_display_name: undefined,
        qualified_opponent_display_name: undefined,
        qualified_opponent_2_display_name: undefined,
        student_study_form: undefined,
        student_study_field: undefined,
        student_study_program: undefined,
        credits_count: undefined,
        application_year: undefined,
      };
    }
    if (!detail) {
      return null;
    }
    return { ...detail };
  }, [dashboardPrefill, detail, isCreate]);

  const { modal, onSaveFailed, onSaveSuccess, onValuesChange } =
    usePhdDirtyLeaveGuard(form, initials);
  const isReadOnly = detail?.is_read_only === true;

  const handleSubmit = async (values) => {
    try {
      await saveProposal({
        id: isEdit ? detail.id : undefined,
        body: values,
      }).unwrap();
      saveSuccess();
      onSaveSuccess(() =>
        navigate(routes.cmsPhdDissertationDefenseExaminerProposals),
      );
    } catch (error) {
      onSaveFailed();
      applyPhdValidationErrors(form, error);
      saveFailed(error);
    }
  };

  const handleDownload = async () => {
    if (!detail?.id) return;
    try {
      const response = detail.is_frozen
        ? await downloadProposalDoc(detail.id).unwrap()
        : await archiveProposalDoc(detail.id).unwrap();
      downloadBlobResponse(
        response,
        `${detail.student_display_name || t('Návrh na oponentov (obhajoba)')}.docx`,
      );
    } catch (error) {
      saveFailed(error);
    }
  };

  const mainActions = useMemo(() => {
    if (!isEdit || !detail) return [];
    return [
      {
        onClick: handleDownload,
        title: detail.is_frozen
          ? t('Stiahnuť DOCX')
          : isAdmin
            ? t('Vydať a stiahnuť archívny DOCX')
            : t('Archívnu verziu môže vydať iba administrátor'),
        icon: <i className="fa fa-download" />,
        permission: 'phd-dissertation-defense-examiner-proposals.index',
        disabled: isDownloading || isArchiving || (!detail.is_frozen && !isAdmin),
      },
    ];
  }, [detail, handleDownload, isAdmin, isArchiving, isDownloading, isEdit, t]);

  if (
    (isEdit && isLoading) ||
    isReferenceLoading ||
    isLoadingApplications ||
    !initials
  ) {
    return <Spinner />;
  }

  return (
    <>
      {!isReadOnly && modal}
      <AppBarCMS
        title={pageTitle}
        backUrl={routes.cmsPhdDissertationDefenseExaminerProposals}
        backUrlTooltip={t('Návrhy oponentov')}
        permissionsBasic="phd-dissertation-defense-examiner-proposals"
        mainActions={mainActions}
      />
      <div style={{ margin: '10px' }}>
        <PhdPageContext
          title={t('Ako funguje návrh komisie pre obhajobu?')}
          description={t(
            'Administrátor doktorandského štúdia vyberie odovzdanú prihlášku na obhajobu a doplní komisiu aj oponentov. Doktorand a školský rok pochádzajú z nadradenej prihlášky.',
          )}
        />
        <AdminContentBlock
          icon="fa fa-users"
          link={routes.cmsPhdDissertationDefenseExaminerProposals}
          title={pageTitle}
        >
          <PhdArchivedRecordNotice record={detail} />
          <Form
            form={form}
            initialValues={initials}
            onFinish={handleSubmit}
            onValuesChange={onValuesChange}
            disabled={isReadOnly}
          >
            <PhdDissertationDefenseExaminerProposalsEditForm
              studentOptions={studentOptions}
              students={students}
              schoolYearOptions={schoolYearOptions}
              ensureSchoolYearSelection={ensureSchoolYearSelection}
              isCreatingSchoolYear={isCreatingSchoolYear}
              examinerOptions={examinerOptions}
              studyFieldLabelOptions={studyFieldLabelOptions}
              applicationOptions={submittedApplications.map((application) => {
                const hasOtherProposal =
                  Boolean(application.examiner_proposal) &&
                  application.examiner_proposal.id !== detail?.id;
                return {
                  value: application.id,
                  disabled: hasOtherProposal,
                  label: `${application.student_display_name || application.phd_student_id} (${application.id})${hasOtherProposal ? ` – ${t('už má priradený návrh')}` : ''}`,
                  application,
                };
              })}
              isSubmitting={isSaving}
              isReadOnly={isReadOnly}
            />
          </Form>
        </AdminContentBlock>
      </div>
    </>
  );
};

export default PhdDissertationDefenseExaminerProposalsEdit;
